
import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ProfessionalDirectoryHeader } from './ProfessionalDirectoryHeader';
import { ProfessionalSearchBar } from './ProfessionalSearchBar';
import { ProfessionalFilterSidebar } from './ProfessionalFilterSidebar';
import { ProfessionalResultsPane } from './ProfessionalResultsPane';
import { ProfessionalFilterDrawer } from './ProfessionalFilterDrawer';
import { useProfessionalDirectoryQuery } from './useProfessionalDirectoryQuery';
import ProfessionalArchiveLinks from './ProfessionalArchiveLinks';

const roleSEO: Record<string, { title: string; description: string; canonical: string }> = {
  caregiver_agency: {
    title: "Find Caregivers & Home Care Agencies",
    description: "Browse trusted caregivers and home care agencies near you. Compare services for personal care, companionship, medication reminders, and daily support for aging at home.",
    canonical: "https://seniorthrive.com/caregivers/directory",
  },
  ot: {
    title: "Find Occupational Therapists",
    description: "Connect with occupational therapists specializing in home safety assessments, fall prevention, adaptive equipment, and aging-in-place modifications near you.",
    canonical: "https://seniorthrive.com/professionals/directory?role=ot",
  },
};

const defaultSEO = {
  title: "Professional Directory",
  description: "Find trusted aging-in-place professionals near you, occupational therapists, care managers, elder law attorneys, home care agencies, and more. Browse verified profiles and connect directly.",
  canonical: "https://seniorthrive.com/professionals/directory",
};

interface ProfessionalDirectoryLayoutProps {
  title?: string;
  description?: string;
}

export function ProfessionalDirectoryLayout({
  title,
  description
}: ProfessionalDirectoryLayoutProps) {
  const [searchParams] = useSearchParams();
  const role = searchParams.get('role') || '';
  const seo = roleSEO[role] || defaultSEO;
  const {
    search,
    setSearch,
    location,
    setLocation,
    radius,
    setRadius,
    licenseVerifiedOnly,
    setLicenseVerifiedOnly,
    sortBy,
    setSortBy,
    filters,
    setFilters,
    results,
    loading,
    totalCount,
    clearAllFilters,
  } = useProfessionalDirectoryQuery();

  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  const handleFiltersApply = (newFilters: typeof filters) => {
    setFilters(newFilters);
  };

  const handleFilterRemove = (key: string, value: string) => {
    const newFilters = { ...filters };
    
    if (key === 'experienceYearsMin') {
      newFilters.experienceYearsMin = undefined;
    } else if (key === 'experienceYearsMax') {
      newFilters.experienceYearsMax = undefined;
    } else if (Array.isArray(newFilters[key as keyof typeof newFilters])) {
      const currentArray = (newFilters[key as keyof typeof newFilters] as string[]) || [];
      (newFilters as any)[key] = currentArray.filter((item: string) => item !== value);
    } else {
      (newFilters as any)[key] = 'all';
    }
    
    setFilters(newFilters);
  };

  const handleFiltersReset = () => {
    clearAllFilters();
    setMobileFiltersOpen(false);
  };

  return (
    <>
      <Helmet>
        <title>{title || seo.title} | SeniorThrive</title>
        <meta name="description" content={description || seo.description} />
        <meta name="keywords" content="professional care, occupational therapist, physical therapist, CAPS, elder law attorney, home modification, financial planning" />
        <link rel="canonical" href={seo.canonical} />
      </Helmet>
      
      <div className="min-h-screen bg-gradient-to-b from-white to-gray-50/50">
        <div className="container mx-auto px-4 py-8">
          <ProfessionalDirectoryHeader />
          
          <ProfessionalSearchBar
            search={search}
            onSearchChange={setSearch}
            location={location}
            onLocationChange={setLocation}
            radius={radius}
            onRadiusChange={setRadius}
            licenseVerifiedOnly={licenseVerifiedOnly}
            onLicenseVerifiedOnlyChange={setLicenseVerifiedOnly}
          />

          {/* Two-column layout: Filters + Results */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* Desktop Filters Sidebar */}
            <div className="hidden lg:block lg:col-span-3">
              <div className="sticky top-8">
                <ProfessionalFilterSidebar
                  filters={filters}
                  onFiltersChange={handleFiltersApply}
                  onFiltersReset={handleFiltersReset}
                />
              </div>
            </div>

            {/* Results */}
            <div className="lg:col-span-9">
              <ProfessionalResultsPane
                results={results}
                loading={loading}
                totalCount={totalCount}
                filters={filters}
                sortBy={sortBy}
                onSortChange={setSortBy}
                onFilterRemove={handleFilterRemove}
                onClearAllFilters={clearAllFilters}
                onShowMobileFilters={() => setMobileFiltersOpen(true)}
              />
            </div>
          </div>

          {/* Crawlable archive of all professional profiles for SEO */}
          <ProfessionalArchiveLinks />

          {/* Cross-navigation to Professional Network */}
          <div className="mt-12 p-6 bg-white rounded-md border shadow-raised">
            <div className="text-center">
              <h2 className="text-lg font-bold text-foreground mb-2">
                Are You a Professional?
              </h2>
              <p className="text-muted-foreground mb-4">
                Join the SeniorThrive network and connect with families who need your expertise.
              </p>
              <Button asChild className="bg-primary hover:bg-black/50">
                <Link to="/professional-network">
                  Join the Professional Network
                </Link>
              </Button>
            </div>
          </div>
        </div>

        {/* Mobile Filter Drawer */}
        <ProfessionalFilterDrawer
          open={mobileFiltersOpen}
          onOpenChange={setMobileFiltersOpen}
          filters={filters}
          onFiltersApply={handleFiltersApply}
          onFiltersReset={handleFiltersReset}
        />
      </div>
    </>
  );
}
